import styles from './AdoptionDashboard.module.css'

// --- Status string constants ---
export const STATUS_ACTIVATED = 'activated'
export const STATUS_PLAN_TO_ACTIVATE = 'plan to activate'
export const STATUS_DO_NOT_PLAN = 'do not plan to activate'
export const STATUS_PAUSED = 'paused'
export const STATUS_INELIGIBLE = 'ineligible'
export const STATUS_MAYBE_PREFIX = 'maybe'

// --- Status colors (read lazily from CSS custom properties — single source of truth) ---
const getCSSVariable = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

let _statusColorsCache: Record<string, string> | null = null

const getStatusColors = (): Record<string, string> => {
  if (!_statusColorsCache) {
    _statusColorsCache = {
      [STATUS_ACTIVATED]: getCSSVariable('--status-color-activated'),
      [STATUS_PLAN_TO_ACTIVATE]: getCSSVariable('--status-color-plan-to-activate'),
      [STATUS_DO_NOT_PLAN]: getCSSVariable('--status-color-do-not-plan'),
      [STATUS_PAUSED]: getCSSVariable('--status-color-paused'),
      [STATUS_INELIGIBLE]: getCSSVariable('--status-color-ineligible'),
    }
  }
  return _statusColorsCache
}

/** Return the hex color for a given status string (used in Excel export). */
export const getStatusColor = (status: string | null): string | null => {
  if (!status) return null
  const lower = status.toLowerCase()
  const colors = getStatusColors()
  if (colors[lower]) return colors[lower]
  if (lower.startsWith(STATUS_MAYBE_PREFIX)) return getCSSVariable('--status-color-maybe')
  return null
}

/** Return the CSS module class for a given status string. */
export const getStatusClass = (status: string | null): string => {
  if (!status) return ''
  const lower = status.toLowerCase()
  if (lower === STATUS_ACTIVATED) return styles.statusActivated
  if (lower === STATUS_PLAN_TO_ACTIVATE) return styles.statusPlanToActivate
  if (lower === STATUS_DO_NOT_PLAN) return styles.statusDoNotPlan
  if (lower === STATUS_PAUSED) return styles.statusPaused
  if (lower === STATUS_INELIGIBLE) return styles.statusIneligible
  if (lower.startsWith(STATUS_MAYBE_PREFIX)) return styles.statusMaybe
  return ''
}

// --- Legend entries (shared between JSX and Excel export) ---
export const LEGEND_ENTRIES: { status: string; definition: string; cssClass?: string }[] = [
  { status: 'Ineligible', definition: "learning can't be scaled due to market/platform constraints" },
  {
    status: 'Paused',
    definition:
      'scaling on hold due to pause on sustainability claims in case of sustainability BPDs, NA OU in Q2 is paused to allocate resources to BU priorities',
  },
  {
    status: 'Activated',
    definition: '>50% of eligible markets in OU have implemented the learning',
    cssClass: styles.legendLabelActivated,
  },
  {
    status: 'Plan to Activate',
    definition: '>50% of eligible markets in OU intend to implement the learning',
    cssClass: styles.legendLabelPlanToActivate,
  },
  {
    status: 'Do Not Plan to Activate',
    definition: '>50% of eligible markets in OU do not intend to implement the learning',
    cssClass: styles.legendLabelDoNotPlan,
  },
]

// --- Helpers ---
export const GLA_STRATEGIC_PRIORITY_LABEL = 'GLA Strategic Priority'

/** Strip "MTO " prefix so "MTO #5 Digital Marketing" displays as "#5 Digital Marketing" (backend data still uses MTO prefix) */
export const formatMTOTitle = (title: string): string => (title.startsWith('MTO #') ? title.slice(4) : title)

export type SortColumn = 'title' | 'decoded_learning' | 'launched_in'
export type SortDirection = 'asc' | 'desc'

/** Convert launched quarter+year to a comparable number for sorting (e.g. Q3 2023 → 2023.3) */
export const launchedSortValue = (quarter: string | null, year: number | null): number => {
  if (!year) return 0
  const q = quarter ? parseInt(quarter.replace(/\D/g, ''), 10) || 0 : 0
  return year + q * 0.1
}

/** Parse a period string like "Q1 2025" into { quarter, year } */
export const parsePeriod = (period: string): { quarter: string; year: number } => {
  const [quarter, yearStr] = period.split(' ')
  return { quarter, year: parseInt(yearStr) }
}
